/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef } from 'react';
import cn from 'classnames';
import useClickOutside from '../effects';
import { Visibility } from '../../../types/common-types';
import { random } from '../../../utils/utils';
import Icon from '../icon';
import './style.scss';

export enum TextFiledType {
  Text = 'text',
  Email = 'email',
  Int = 'int',
  Float = 'float',
  Phone = 'phone',
  Password = 'password',
}

interface TextFiledProps {
  id?: string;
  name?: string;
  label?: string;
  value: string;
  error?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  regexp?: string;
  helperText?: string;
  required?: boolean;
  readOnly?: boolean;
  type?: TextFiledType;
  visibility?: Visibility;
  length?: number;
  className?: string;
  styleName?: string;
  rightIcon?: string;
  nonPasteable?: boolean;
  onChange?: (value: { id: string; value: string; error: boolean }) => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onKeyUp?: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const TextField: React.FC<TextFiledProps> = ({
  id = '',
  value,
  placeholder,
  label,
  visibility = Visibility.Visible,
  onKeyPress,
  onKeyDown,
  onKeyUp,
  onBlur,
  onChange,
  error = false,
  helperText,
  regexp,
  type,
  length,
  required,
  readOnly = false,
  onFocus,
  className,
  rightIcon,
  nonPasteable,
}) => {
  const [focus, setFocus] = useState(false);
  const inputEl = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef(null);
  const handleFocus = () => {
    if (visibility === Visibility.Disabled) return;
    if (inputEl != null && inputEl.current != null) {
      inputEl.current.focus();
      setFocus(true);
      if (onFocus) onFocus();
    }
  };
  const handleBlur = () => {
    if (visibility === Visibility.Disabled) return;
    setFocus(false);
    if (onBlur) onBlur();
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value: inputValue } = e.target;
    let hasError = false;

    if (type === TextFiledType.Email) {
      // eslint-disable-next-line max-len
      const re = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i; // eslint-disable-line
      if (!re.test(String(inputValue))) {
        hasError = true;
      }
    }
    // Formatting type int
    if (
      type === TextFiledType.Int ||
      (type === TextFiledType.Phone && /\D/g.test(inputValue))
    ) {
      inputValue = inputValue.replace(/\D/g, '');
    }
    // Formatting type float
    if (type === TextFiledType.Float) {
      inputValue = inputValue
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*)\./g, '$1');
    }
    // Formatting string length
    if (length && inputValue.length >= length) {
      inputValue = inputValue.substr(0, length);
    }
    // check regexp
    if (regexp) {
      const r = new RegExp(regexp);
      hasError = !r.test(inputValue.trim());
    }
    if (required && (!inputValue || !inputValue.length)) {
      hasError = true;
    }
    if (onChange) onChange({ id, value: inputValue, error: hasError });
  };
  useClickOutside(wrapperRef, () => {
    if (value && value !== value.trim()) {
      if (onChange) onChange({ id, value: value.trim(), error });
    }
    setFocus(false);
    if (onBlur) onBlur();
  });
  // eslint-disable-next-line consistent-return
  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (nonPasteable) {
      e.preventDefault();
      return false;
    }
    return true;
  };
  const tabIndex = visibility === Visibility.Visible ? 0 : -1;
  let style = {};
  if (readOnly || visibility !== Visibility.Visible) {
    style = { pointerEvents: 'none' };
  }
  const uniqId = random();
  const uId = `id-${uniqId}`;
  return (
    <div
      id={id}
      data-cy="edit"
      className={className}
      styleName={cn('txt', {
        disabled: visibility === Visibility.Disabled,
        hidden: visibility === Visibility.Hidden,
        error,
        focus,
      })}
    >
      <label styleName="txt__label" className="label" htmlFor={uId}>
        {label}
      </label>
      <div
        styleName="txt__field"
        className="field"
        ref={wrapperRef}
        onClick={handleFocus}
        onKeyPress={onKeyPress}
      >
        <input
          id={uId}
          ref={inputEl}
          style={style}
          tabIndex={tabIndex}
          readOnly={readOnly}
          type={
            type === TextFiledType.Password
              ? TextFiledType.Password
              : TextFiledType.Text
          }
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={handleFocus}
          disabled={visibility === Visibility.Disabled}
          onPaste={onPaste}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {rightIcon ? (
          <div styleName="txt__rightIcon" className="rightIcon">
            <Icon value={rightIcon} />
          </div>
        ) : null}
      </div>
      {helperText ? (
        <div data-cy-prop="errorMsg" styleName="txt__helperText">
          {helperText}
        </div>
      ) : null}
    </div>
  );
};

export default TextField;
